import { useEffect } from "react";

// Simple in-memory registry mapping a path segment (typically a UUID or slug)
// to a human-readable label. Used by AppHeader to render breadcrumb names
// instead of raw IDs from the URL.
const labels = new Map<string, string>();
const listeners = new Set<() => void>();
let version = 0;

export function setBreadcrumbLabel(segment: string, label: string) {
  if (!segment) return;
  if (labels.get(segment) === label) return;
  labels.set(segment, label);
  version += 1;
  listeners.forEach((l) => l());
}

export function clearBreadcrumbLabel(segment: string) {
  if (!segment) return;
  if (!labels.has(segment)) return;
  labels.delete(segment);
  version += 1;
  listeners.forEach((l) => l());
}

export function getBreadcrumbLabel(segment: string): string | undefined {
  return labels.get(segment);
}

export function getBreadcrumbLabelsVersion() {
  return version;
}

export function subscribeBreadcrumbLabels(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Register a breadcrumb label for a dynamic path segment (e.g. a UUID). */
export function useBreadcrumbLabel(segment: string | undefined, label: string | undefined) {
  useEffect(() => {
    if (!segment || !label) return;
    setBreadcrumbLabel(segment, label);
    // When the page unmounts (user navigates away) drop the registration so
    // a stale label can't leak into another route's breadcrumb trail.
    return () => {
      clearBreadcrumbLabel(segment);
    };
  }, [segment, label]);
}
